import React, { useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../redux/store';
import { IChat, IUser, IMessage, IConnectionRequest } from '../../interfaces/IChatUser';
import { useChatSystem } from '../../hooks/useChatSystem';
import {
    setChats,
    addChat,
    removeChat,
    addMessage,
    setIsTyping,
    updateMessageStatus,
    markMessagesAsRead,
    clearUnreadCount,
    setReconnecting,
    updateMessageReactions,
} from '../../redux/chatSlice';
import { bumpScoreCheckVersion } from '../../redux/scoreSlice';
import ChatWindow from './ChatWindow';
import { useApiClient } from '../../ApiClientProvider';
import { ConnectionService } from '../../services/connectionService';
import { ConversationService } from '../../services/conversationService';
import { useSession } from '../../SessionContext';
import { ProfileService } from '../../services/profileService';

interface ChatManagerAPI {
    startChat: (user: IUser) => void;
    getUnreadCounts: () => { [userId: string]: number };
    sendTypingToUser: (userId: string) => Promise<void>;
    acceptRequest: (requestId: string) => void;
    rejectRequest: (requestId: string) => void;
    cancelRequest: (requestId: string) => void;
    sendRequest: (otherUserId: string, eventId?: string) => Promise<void>;
    getConnectionRequests: () => IConnectionRequest[];
}

interface ChatManagerProps {
    onUnreadCountChange?: (count: number) => void;
    onStartChat?: (user: IUser) => void;
    onAcceptRequest?: (id: string) => void;
    onRejectRequest?: (id: string) => void;
    onCancelRequest?: (id: string) => void;
}

const ChatManager: React.FC<ChatManagerProps> = ({
    onUnreadCountChange,
    onStartChat,
    onAcceptRequest,
    onRejectRequest,
}) => {
    const dispatch = useDispatch();
    const apiClient = useApiClient('applicationClient');
    const managementClient = useApiClient('managementClient');
    const { session } = useSession();

    const currentUserId = session?.user?.id || '';
    const currentUser: IUser = {
        id: currentUserId,
        name: session?.user?.name || 'You',
        avatar: '',
        isOnline: true
    };

    const connectionService = React.useMemo(() => new ConnectionService(apiClient), [apiClient]);
    const conversationService = React.useMemo(() => new ConversationService(apiClient), [apiClient]);
    const profileService = React.useMemo(() => new ProfileService(managementClient), [managementClient]);
    const activeChats = useSelector((state: RootState) => state.chat.chats);
    const unreadCounts = useSelector((state: RootState) => state.chat.unreadCounts);
    const connectionRequests = useSelector((state: RootState) => state.chat.connectionRequests);
    const typingTimeouts = useRef<{ [userId: string]: NodeJS.Timeout }>({});

    const activeChatsRef = useRef(activeChats);
    useEffect(() => {
        activeChatsRef.current = activeChats;
    }, [activeChats]);

    useEffect(() => {
        if (!session) {
            dispatch(setChats([]));
        }
        const fetchProfileImage = async () => {
            if (session) {
                if (session?.user?.id) {
                    try {
                        const image = await profileService.getProfileImage();
                        currentUser.avatar = image;
                    } catch (error) {
                        console.error('Failed to fetch profile image:', error);
                    }
                }
            }
        }
        fetchProfileImage();
    }, [session, dispatch]);

    const {
        sendTypingToUser,
        sendStopTypingToUser,
        handleAcceptRequest,
        handleRejectRequest,
        handleCancelRequest,
        handleSendRequest,
    } = useChatSystem({
        onMessageForUI: async (fromUserId, content) => {
            const newMessage: IMessage = {
                id: `msg-${Date.now()}`,
                senderId: fromUserId,
                content,
                timestamp: new Date().toISOString(),
                isRead: false,
            };

            const currentChats = activeChatsRef.current;
            let chat = currentChats.find(chat => chat.participants.some(p => p.id === fromUserId));

            if (chat) {
                dispatch(addMessage({ chatId: chat.id, message: newMessage }));
                // Clear typing indicator immediately when message arrives
                if (typingTimeouts.current[fromUserId]) {
                    clearTimeout(typingTimeouts.current[fromUserId]);
                    delete typingTimeouts.current[fromUserId];
                }
                dispatch(setIsTyping({ chatId: chat.id, isTyping: false }));
                // Clear unread count if chat window is open (not minimized)
                if (!chat.isMinimized) {
                    dispatch(clearUnreadCount(fromUserId));
                    // The thread is open and visible: persist the read on the server so
                    // the sender receives a live read receipt for messages sent into an
                    // already-open window (their tick flips to 'read'). Guard against
                    // fallback ids that aren't real conversations.
                    if (!chat.id.startsWith('chat-')) {
                        conversationService.markConversationAsRead(chat.id).catch(() => undefined);
                    }
                }
                if (chat.isMinimized) {
                    dispatch(setChats(currentChats.map(c =>
                        c.id === chat.id ? { ...c, isMinimized: false } : c
                    )));
                }
            } else {
                try {
                    const connection = await connectionService.get(fromUserId);
                    const conversationId = connection.conversationId || `chat-${fromUserId}`;

                    let messages: IMessage[] = [];
                    if (connection.conversationId) {
                        try {
                            messages = await conversationService.getMessages(connection.conversationId);
                        } catch (error) {
                            console.warn('Failed to fetch messages:', error);
                        }
                    }

                    const otherUserName = connection.otherUserName || `User-${fromUserId.substring(0, 8)}`;

                    const newChat: IChat = {
                        id: conversationId,
                        participants: [
                            currentUser,
                            {
                                id: fromUserId,
                                name: otherUserName,
                                avatar: connection.otherUserImage || "",
                                isOnline: true,
                                lastSeen: ""
                            }
                        ],
                        messages,
                        isMinimized: false,
                        unreadCount: 1,
                        lastActivity: new Date().toISOString(),
                        isTyping: false,
                    };

                    dispatch(addChat(newChat));
                    // Add the new SignalR message separately so deduplication logic in reducer runs
                    dispatch(addMessage({ chatId: conversationId, message: newMessage }));
                    // The chat auto-opens visible (not minimized), so persist the read on
                    // the server. This sends a live read receipt to the sender (their tick
                    // flips to 'read'). Only for real conversations, not fallback ids.
                    if (connection.conversationId) {
                        conversationService.markConversationAsRead(connection.conversationId).catch(() => undefined);
                    }
                    // Clear typing indicator
                    if (typingTimeouts.current[fromUserId]) {
                        clearTimeout(typingTimeouts.current[fromUserId]);
                        delete typingTimeouts.current[fromUserId];
                    }
                } catch (error) {
                    console.error('Failed to load chat:', error);
                    const fallbackChat: IChat = {
                        id: `chat-${fromUserId}`,
                        participants: [
                            currentUser,
                            {
                                id: fromUserId,
                                name: "User",
                                avatar: "",
                                isOnline: true,
                                lastSeen: ""
                            }
                        ],
                        messages: [newMessage],
                        isMinimized: false,
                        unreadCount: 1,
                        lastActivity: new Date().toISOString(),
                        isTyping: false,
                    };
                    dispatch(addChat(fallbackChat));
                    // Clear typing indicator
                    if (typingTimeouts.current[fromUserId]) {
                        clearTimeout(typingTimeouts.current[fromUserId]);
                        delete typingTimeouts.current[fromUserId];
                    }
                }
            }
        },
        onUserTyping: async (fromUserId) => {
            console.log('👀 Typing event received from:', fromUserId);

            // Use activeChatsRef to check current state, not stale state variable
            const currentChats = activeChatsRef.current;
            console.log(`🔍 Searching in ${currentChats.length} open chats...`);

            let chat = currentChats.find(chat => {
                const hasUser = chat.participants.some(p => p.id === fromUserId);
                if (hasUser) {
                    console.log(`  ✅ Chat "${chat.id}" has participant ${fromUserId}`);
                }
                return hasUser;
            });

            if (chat) {
                console.log('✅ Setting isTyping=true for chat:', chat.id);
                dispatch(setIsTyping({ chatId: chat.id, isTyping: true }));
                if (typingTimeouts.current[fromUserId]) {
                    clearTimeout(typingTimeouts.current[fromUserId]);
                }
                // Safety net for a lost stop-typing event. Kept comfortably above the
                // sender's 2s typing heartbeat so network jitter can't flicker the
                // indicator off between pings.
                typingTimeouts.current[fromUserId] = setTimeout(() => {
                    console.log('⏰ Typing timeout expired, setting isTyping=false for chat:', chat.id);
                    dispatch(setIsTyping({ chatId: chat.id, isTyping: false }));
                }, 5000);
            } else {
                console.warn(`⚠️ No chat found for typing user "${fromUserId}".`);
            }
        },
        onUserStoppedTyping: (fromUserId) => {
            const currentChats = activeChatsRef.current;
            const chat = currentChats.find(c => c.participants.some(p => p.id === fromUserId));
            if (!chat) return;
            if (typingTimeouts.current[fromUserId]) {
                clearTimeout(typingTimeouts.current[fromUserId]);
                delete typingTimeouts.current[fromUserId];
            }
            dispatch(setIsTyping({ chatId: chat.id, isTyping: false }));
        },
        onMessagesRead: (conversationId) => {
            // The other participant read the conversation: flip my delivered
            // bubbles in this chat to 'read'.
            const currentChats = activeChatsRef.current;
            const chat = currentChats.find(c => c.id === conversationId);
            if (!chat) return;
            dispatch(markMessagesAsRead({ chatId: chat.id }));
        },
        onMessageReactionChanged: (payload) => {
            // Update message reactions when someone adds/removes a reaction
            const currentChats = activeChatsRef.current;
            // Find chat containing this message (we don't have conversationId in payload, so check all chats)
            for (const chat of currentChats) {
                const message = chat.messages.find(m => m.id === payload.messageId);
                if (message) {
                    // Preserve current user's reaction state and only update when payload is for current user
                    const userReaction = payload.userId === currentUserId
                        ? (payload.action === 'Removed' ? undefined : payload.reactionType)
                        : message.reactions?.userReaction;
                    
                    dispatch(updateMessageReactions({
                        chatId: chat.id,
                        messageId: payload.messageId,
                        reactions: {
                            counts: payload.counts,
                            userReaction,
                        }
                    }));
                    break;
                }
            }
        },
        onConnected: () => {
            dispatch(setReconnecting(false));
            // Recover messages missed while disconnected: refetch the open,
            // non-minimized conversations and merge them in. addMessage dedups,
            // so already-shown messages are ignored and only missed ones appear.
            const chatsToSync = activeChatsRef.current.filter(
                c => !c.isMinimized && !c.id.startsWith('chat-')
            );
            chatsToSync.forEach(async (chat) => {
                try {
                    const fresh = await conversationService.getMessages(chat.id);
                    fresh.forEach(message => dispatch(addMessage({ chatId: chat.id, message })));
                } catch {
                    // best-effort recovery
                }
            });
        },
        onDisconnected: () => {
            dispatch(setReconnecting(true));
        }
    });

    // Notify parent of unread count changes
    useEffect(() => {
        onUnreadCountChange?.(Object.values(unreadCounts).reduce((sum, count) => sum + count, 0));
    }, [unreadCounts, onUnreadCountChange]);

    const handleStartChat = useCallback(async (user: IUser) => {
        // Check if chat with this user already exists
        const existingChat = activeChats.find(chat =>
            chat.participants.some(p => p.id === user.id)
        );

        if (existingChat) {
            // If chat exists but is minimized, un-minimize it
            if (existingChat.isMinimized) {
                dispatch(setChats(activeChats.map(c =>
                    c.id === existingChat.id ? { ...c, isMinimized: false } : c
                )));
            }
            dispatch(clearUnreadCount(user.id));
            onStartChat?.(user);
            return;
        }

        try {
            const connection = await connectionService.get(user.id);
            const conversationId = connection.conversationId || `chat-${user.id}`;

            let messages: IMessage[] = [];
            if (connection.conversationId) {
                try {
                    messages = await conversationService.getMessages(connection.conversationId);
                } catch (error) {
                    console.warn('Failed to fetch messages:', error);
                }

                // Mark conversation as read when opening it
                // This persists the read state on the server so unread count remains accurate
                try {
                    await conversationService.markConversationAsRead(connection.conversationId);
                    // Only clear unread count if server update succeeded
                    dispatch(clearUnreadCount(user.id));
                } catch (error) {
                    console.error('Failed to mark conversation as read:', error);
                    // Don't clear unread count locally if server update failed
                    // This prevents confusion when unread badge reappears after refresh
                }
            }

            const newChat: IChat = {
                id: conversationId,
                participants: [currentUser, user],
                messages,
                isMinimized: false,
                unreadCount: 0,
                lastActivity: messages.length > 0 ? messages[messages.length - 1].timestamp : new Date().toISOString(),
                isTyping: false,
            };

            dispatch(addChat(newChat));
            onStartChat?.(user);
        } catch (error) {
            console.error('Failed to start chat:', error);
            const newChat: IChat = {
                id: `chat-${user.id}`,
                participants: [currentUser, user],
                messages: [],
                isMinimized: false,
                unreadCount: 0,
                lastActivity: new Date().toISOString(),
                isTyping: false,
            };
            dispatch(addChat(newChat));
            dispatch(clearUnreadCount(user.id));
            onStartChat?.(user);
        }
    }, [dispatch, onStartChat, connectionService, conversationService, currentUser, activeChats]);

    const handleCloseChat = useCallback((chatId: string) => {
        dispatch(removeChat(chatId));
    }, [dispatch]);

    const handleMinimizeChat = useCallback((chatId: string) => {
        const chat = activeChats.find(chat => chat.id === chatId);
        if (chat) {
            dispatch(setChats(activeChats.map(c =>
                c.id === chatId ? { ...c, isMinimized: !c.isMinimized } : c
            )));
        }
    }, [dispatch, activeChats]);

    const handleSendMessage = useCallback(async (chatId: string, content: string) => {
        const chat = activeChats.find(c => c.id === chatId);
        if (!chat) return;
        const recipient = chat.participants.find(p => p.id !== currentUserId);
        if (!recipient) return;

        // A GUID doubles as the local bubble key AND the server idempotency key, so a
        // retried send reconciles to the same message instead of creating a duplicate.
        const clientId = crypto.randomUUID();

        // Optimistically render the bubble immediately so the UI feels instant.
        const optimisticMessage: IMessage = {
            id: clientId,
            clientId,
            senderId: currentUserId,
            content,
            timestamp: new Date().toISOString(),
            isRead: false,
            status: 'sending',
        };
        dispatch(addMessage({ chatId, message: optimisticMessage }));

        // The author has finished composing once they send.
        sendStopTypingToUser(recipient.id).catch(() => { /* best-effort */ });

        try {
            // The REST endpoint persists the message AND broadcasts the real-time
            // "ReceiveMessage" event to the recipient. We must NOT also push it via
            // the hub, or the recipient receives the same message twice.
            const response = await conversationService.sendMessage(recipient.id, content, clientId);
            dispatch(updateMessageStatus({ chatId, clientId, messageId: response.messageId, status: 'sent' }));
            // A sent message can trigger "first message", "reply within 48h" or "reconnect"
            // scoring (network-score.md section 3) — re-check for a celebration immediately.
            dispatch(bumpScoreCheckVersion());
        } catch (error) {
            console.error('Failed to send message:', error);
            dispatch(updateMessageStatus({ chatId, clientId, status: 'failed' }));
        }
    }, [activeChats, dispatch, sendStopTypingToUser, conversationService, currentUserId]);

    const handleRetryMessage = useCallback(async (chatId: string, clientId: string) => {
        const chat = activeChats.find(c => c.id === chatId);
        if (!chat) return;
        const message = chat.messages.find(m => m.clientId === clientId);
        if (!message) return;
        const recipient = chat.participants.find(p => p.id !== currentUserId);
        if (!recipient) return;

        dispatch(updateMessageStatus({ chatId, clientId, status: 'sending' }));

        try {
            // Single delivery path: the REST endpoint persists and broadcasts the
            // real-time event to the recipient (see handleSendMessage). Reuse the
            // original clientId as the idempotency key so a retry the server already
            // persisted returns the existing message, not a duplicate.
            const response = await conversationService.sendMessage(recipient.id, message.content, clientId);
            dispatch(updateMessageStatus({ chatId, clientId, messageId: response.messageId, status: 'sent' }));
            dispatch(bumpScoreCheckVersion());
        } catch (error) {
            console.error('Failed to retry message:', error);
            dispatch(updateMessageStatus({ chatId, clientId, status: 'failed' }));
        }
    }, [activeChats, dispatch, conversationService, currentUserId]);

    // Store methods in a ref so parent can access them
    const managerRef = useRef<ChatManagerAPI>({
        startChat: handleStartChat,
        getUnreadCounts: () => unreadCounts,
        sendTypingToUser,
        acceptRequest: handleAcceptRequest,
        rejectRequest: handleRejectRequest,
        cancelRequest: handleCancelRequest,
        sendRequest: handleSendRequest,
        getConnectionRequests: () => connectionRequests,
    });

    useEffect(() => {
        managerRef.current = {
            startChat: handleStartChat,
            getUnreadCounts: () => unreadCounts,
            sendTypingToUser,
            acceptRequest: handleAcceptRequest,
            rejectRequest: handleRejectRequest,
            cancelRequest: handleCancelRequest,
            sendRequest: handleSendRequest,
            getConnectionRequests: () => connectionRequests,
        };
        window.chatManager = managerRef.current;
    }, [handleStartChat, unreadCounts, sendTypingToUser, handleAcceptRequest, handleRejectRequest, handleCancelRequest, handleSendRequest, connectionRequests]);

    const handleTyping = useCallback(async (chatId: string) => {
        const chat = activeChats.find(c => c.id === chatId);
        if (!chat) return;
        const recipient = chat.participants.find(p => p.id !== currentUserId);
        if (!recipient) return;

        console.log('⌨️ Sending typing indicator to user:', recipient.id);
        await sendTypingToUser(recipient.id);
    }, [activeChats, sendTypingToUser, currentUserId]);

    const handleStopTyping = useCallback(async (chatId: string) => {
        const chat = activeChats.find(c => c.id === chatId);
        if (!chat) return;
        const recipient = chat.participants.find(p => p.id !== currentUserId);
        if (!recipient) return;
        await sendStopTypingToUser(recipient.id);
    }, [activeChats, sendStopTypingToUser, currentUserId]);
    const chatWindows = activeChats.map((chat) => (
        <ChatWindow
            key={chat.id}
            chat={chat}
            currentUserId={currentUserId}
            onClose={handleCloseChat}
            onMinimize={handleMinimizeChat}
            onSendMessage={handleSendMessage}
            onTyping={handleTyping}
            onStopTyping={handleStopTyping}
            onRetryMessage={handleRetryMessage}
            style={{
                right: 120,
                zIndex: 1500
            }}
        />
    ));

    return createPortal(
        <div>{chatWindows}</div>,
        document.body
    );
};

// Extend window type for TypeScript

declare global {
    interface Window {
        chatManager: ChatManagerAPI;
        chatHub?: {
            sendTyping: (userId: string) => Promise<void>;
            sendStopTyping?: (userId: string) => Promise<void>;
            getOnlineUsers?: () => Promise<string[]>;
        };
        store?: any;
    }
}

export default ChatManager;